import { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";

const VIDEO_URL = "/media/frota-gorillas.mp4";
const POSTER_URL = "/media/frota-poster.jpg";

export default function VideoShowcase() {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);

  // Play when visible, pause when off-screen (perf + battery)
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          v.play().catch(() => {});
        } else {
          v.pause();
        }
      },
      { threshold: 0.25 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setIsPlaying(true);
    } else {
      v.pause();
      setIsPlaying(false);
    }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setIsMuted(v.muted);
  };

  return (
    <section
      id="video"
      data-testid="video-section"
      className="relative overflow-hidden bg-brand-black py-24 sm:py-32"
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="grid grid-cols-12 gap-6 border-b border-chrome/15 pb-8">
          <div className="col-span-12 flex items-center gap-4 sm:col-span-4">
            <span className="font-mono text-xs tracking-[0.3em] text-brand-yellow">
              [ AO VIVO · FROTA ]
            </span>
            <span className="h-px flex-1 bg-chrome/20" />
          </div>
          <h2
            data-testid="video-h2"
            className="col-span-12 font-display text-4xl uppercase leading-[0.9] tracking-tight text-white sm:col-span-8 sm:text-6xl"
          >
            A nossa <span className="text-brand-yellow">frota</span> em ação
          </h2>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative mt-10"
        >
          {/* glow */}
          <div
            aria-hidden="true"
            className="absolute inset-x-10 -bottom-10 -z-10 h-40 bg-brand-yellow/20 blur-[110px]"
          />

          <div className="relative mx-auto aspect-[3/4] w-full max-w-[440px] overflow-hidden border border-chrome/25 bg-neutral-950 sm:aspect-[4/5] lg:aspect-[9/16] lg:max-w-[420px]">
            <video
              ref={videoRef}
              data-testid="showcase-video"
              src={VIDEO_URL}
              poster={POSTER_URL}
              className="absolute inset-0 h-full w-full object-cover"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              aria-label="Vídeo da frota GORILLAS REBOQUE em atendimento"
            />

            {/* frame corners */}
            <span className="pointer-events-none absolute left-4 top-4 h-5 w-5 border-l-2 border-t-2 border-brand-yellow" />
            <span className="pointer-events-none absolute right-4 top-4 h-5 w-5 border-r-2 border-t-2 border-brand-yellow" />
            <span className="pointer-events-none absolute left-4 bottom-4 h-5 w-5 border-l-2 border-b-2 border-brand-yellow" />
            <span className="pointer-events-none absolute right-4 bottom-4 h-5 w-5 border-r-2 border-b-2 border-brand-yellow" />

            {/* Top badges */}
            <div className="pointer-events-none absolute left-0 right-0 top-0 flex items-start justify-between p-5">
              <div className="pointer-events-auto inline-flex items-center gap-2 bg-brand-yellow px-3 py-1.5">
                <span className="h-2 w-2 animate-pulse rounded-full bg-black" />
                <span className="font-mono text-[10px] font-bold tracking-[0.3em] text-black">
                  ATENDIMENTO REAL
                </span>
              </div>
              <span className="hidden font-mono text-[10px] tracking-[0.3em] text-white/70 sm:inline">
                GORILLAS · REBOQUE · 24H
              </span>
            </div>

            {/* Bottom controls */}
            <div className="absolute inset-x-0 bottom-0 flex items-end justify-between bg-gradient-to-t from-black/80 to-transparent p-5">
              <span className="font-mono text-[10px] tracking-[0.3em] text-white/80">
                BELÉM / PA · FROTA GORILLAS
              </span>
              <div className="flex items-center gap-2">
                <button
                  data-testid="video-play-toggle"
                  onClick={togglePlay}
                  aria-label={isPlaying ? "Pausar vídeo" : "Reproduzir vídeo"}
                  className="inline-flex h-10 w-10 items-center justify-center border border-chrome/40 bg-black/60 text-white transition-colors hover:border-brand-yellow hover:text-brand-yellow"
                >
                  {isPlaying ? (
                    <Pause className="h-4 w-4" />
                  ) : (
                    <Play className="h-4 w-4" />
                  )}
                </button>
                <button
                  data-testid="video-mute-toggle"
                  onClick={toggleMute}
                  aria-label={isMuted ? "Ativar som" : "Silenciar"}
                  className="inline-flex h-10 w-10 items-center justify-center border border-chrome/40 bg-black/60 text-white transition-colors hover:border-brand-yellow hover:text-brand-yellow"
                >
                  {isMuted ? (
                    <VolumeX className="h-4 w-4" />
                  ) : (
                    <Volume2 className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
